import type { z } from 'zod';

import { apiClient } from '@/lib/api-client';
import { ApiError } from '@/lib/errors';

import {
  redFlagSchema,
  type PaginatedRedFlags,
  type RaiseRedFlagInput,
  type RedFlag,
  type RedFlagListParams,
} from '../types';

function parseOrThrow<T>(schema: z.ZodType<T>, raw: unknown, context: string): T {
  const result = schema.safeParse(raw);
  if (!result.success) {
    const first = result.error.issues[0];
    const path = first?.path.join('.') ?? '';
    const detail = first
      ? `${path ? `${path}: ` : ''}${first.message}`
      : 'Unexpected response shape';
    if (import.meta.env.DEV) {
      console.error(`[redFlagsService] ${context} response failed schema parse:`, {
        issues: result.error.issues,
        raw,
      });
    }
    throw new ApiError({
      status: 0,
      code: 'SHAPE_MISMATCH',
      message: `${context}: ${detail}`,
    });
  }
  return result.data;
}

export const redFlagsService = {
  async list(params: RedFlagListParams = {}): Promise<PaginatedRedFlags> {
    const { data } = await apiClient.get<{
      data: unknown[];
      pagination?: { page: number; limit: number; total: number };
    }>('/red-flags', { params });
    const rows: RedFlag[] = data.data.map((row) => parseOrThrow(redFlagSchema, row, 'list'));
    return {
      data: rows,
      pagination: data.pagination ?? { page: 1, limit: rows.length, total: rows.length },
    };
  },

  async createFlag(clientId: string, input: RaiseRedFlagInput): Promise<RedFlag> {
    const { data } = await apiClient.post<{ data: unknown }>(
      `/clients/${clientId}/red-flags`,
      input,
    );
    return parseOrThrow(redFlagSchema, data.data, 'createFlag');
  },

  async listClientFlags(
    clientId: string,
    params: RedFlagListParams = {},
  ): Promise<PaginatedRedFlags> {
    const { data } = await apiClient.get<{
      data: unknown[];
      pagination?: { page: number; limit: number; total: number };
    }>(`/clients/${clientId}/red-flags`, { params });
    const rows: RedFlag[] = data.data.map((row) =>
      parseOrThrow(redFlagSchema, row, 'listClientFlags'),
    );
    return {
      data: rows,
      pagination: data.pagination ?? { page: 1, limit: rows.length, total: rows.length },
    };
  },

  /**
   * PATCH /clients/:clientId/red-flags/:flagId/close. Chief-only on the BE
   * (gated by `red_flag:close`). `actionTaken` is required (1..5000 chars)
   * and is what the chief writes describing what they did to close out the
   * flag — visible to readers under the resolved fields.
   */
  async closeFlag({
    clientId,
    flagId,
    actionTaken,
  }: {
    clientId: string;
    flagId: string;
    actionTaken: string;
  }): Promise<RedFlag> {
    const { data } = await apiClient.patch<{ data: unknown }>(
      `/clients/${clientId}/red-flags/${flagId}/close`,
      { actionTaken },
    );
    return parseOrThrow(redFlagSchema, data.data, 'closeFlag');
  },
};
